import React, { useState, useMemo } from 'react';
import { normalizeText } from '../utils/dataUtils';
import { CHECKOUT_MOODS } from '../utils/daycareUtils';

const CheckOutWizard = ({ visits = [], loading, editingVisit = null, onSubmit, onCancel }) => {
    const [step, setStep] = useState(editingVisit ? 2 : 1);
    const [pickedVisit, setPickedVisit] = useState(editingVisit);
    const [notes, setNotes] = useState(editingVisit?.notes || '');
    const [checkoutMood, setCheckoutMood] = useState(editingVisit?.checkoutMood || null);
    const [checkoutBehaviorNotes, setCheckoutBehaviorNotes] = useState(editingVisit?.checkoutBehaviorNotes || '');
    const [searchTerm, setSearchTerm] = useState('');

    const activeVisits = useMemo(() =>
        visits.filter(v => v.checkInTime && !v.checkOutTime),
        [visits]
    );

    const filteredVisits = useMemo(() => {
        const term = normalizeText(searchTerm);
        return activeVisits.filter(v =>
            !term ||
            normalizeText(v.dog?.name).includes(term) ||
            normalizeText(v.dog?.breed).includes(term) ||
            v.dog?.owners?.some(o => normalizeText(o.name).includes(term))
        );
    }, [activeVisits, searchTerm]);

    const fmtTime = (t) =>
        t ? new Date(t).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '';

    const getDuration = (from) => {
        if (!from) return '';
        const mins = Math.round((Date.now() - new Date(from)) / 60000);
        if (mins < 60) return `${mins}m`;
        const h = Math.floor(mins / 60), m = mins % 60;
        return m ? `${h}h ${m}m` : `${h}h`;
    };

    const handleConfirm = () =>
        onSubmit({ visitId: pickedVisit._id, notes, checkoutMood, checkoutBehaviorNotes });

    const ownerNames = pickedVisit?.dog?.owners?.map(o => o.name).join(', ') || '—';
    const now = new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

    return (
        <div className="ci-wizard">

            {/* ── STEP 1: Pick a dog ── */}
            {step === 1 && (
                <div className="ci-step-body">
                    <h2 className="ci-step-title">¿Quién se va a casa?</h2>
                    <div className="ci-search-wrap">
                        <span className="ci-search-icon">🔍</span>
                        <input
                            className="ci-search"
                            placeholder="Buscar por nombre, raza o propietario…"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                    </div>
                    {activeVisits.length === 0 ? (
                        <p className="ci-empty">No hay perros con entrada registrada.</p>
                    ) : filteredVisits.length === 0 ? (
                        <p className="ci-empty">Ningún perro coincide con tu búsqueda.</p>
                    ) : (
                        <div className="ci-dog-list">
                            {filteredVisits.map(visit => (
                                <button
                                    key={visit._id}
                                    className="ci-dog-card"
                                    onClick={() => { setPickedVisit(visit); setStep(2); }}
                                >
                                    <div className="ci-dog-avatar">
                                        {visit.dog?.pictureUrl || visit.dog?.picture
                                            ? <img src={visit.dog.pictureUrl || visit.dog.picture} alt={visit.dog?.name} />
                                            : <span>🐶</span>
                                        }
                                    </div>
                                    <div className="ci-dog-info">
                                        <span className="ci-dog-name">{visit.dog?.name}</span>
                                        <span className="ci-dog-meta">
                                            {visit.dog?.breed} · {getDuration(visit.checkInTime)} · desde las {fmtTime(visit.checkInTime)}
                                        </span>
                                    </div>
                                    <span className="ci-dog-chevron">›</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="ci-actions">
                        <button className="btn-secondary" onClick={onCancel}>Cancelar</button>
                    </div>
                </div>
            )}

            {/* ── STEP 2: Review & confirm ── */}
            {step === 2 && pickedVisit && (
                <div className="ci-step-body">
                    <h2 className="ci-step-title">Resumen de salida</h2>

                    <div className="ci-selected-bar">
                        <div className="ci-dog-avatar">
                            {pickedVisit.dog?.pictureUrl || pickedVisit.dog?.picture
                                ? <img src={pickedVisit.dog.pictureUrl || pickedVisit.dog.picture} alt={pickedVisit.dog?.name} />
                                : <span>🐶</span>
                            }
                        </div>
                        <div className="ci-dog-info">
                            <span className="ci-dog-name">{pickedVisit.dog?.name}</span>
                            <span className="ci-dog-meta">{ownerNames}</span>
                        </div>
                        {!editingVisit && (
                            <button className="ci-change-btn" onClick={() => setStep(1)}>Cambiar</button>
                        )}
                    </div>

                    <div className="ci-review-card">
                        <div className="ci-kv">
                            <span className="ci-k">Entrada</span>
                            <span className="ci-v">{fmtTime(pickedVisit.checkInTime)}</span>
                        </div>
                        <div className="ci-kv">
                            <span className="ci-k">Salida</span>
                            <span className="ci-v">{now}</span>
                        </div>
                        <div className="ci-kv">
                            <span className="ci-k">Tiempo total</span>
                            <span className="ci-v co-duration">{getDuration(pickedVisit.checkInTime)}</span>
                        </div>
                        {pickedVisit.package && (
                            <div className="ci-kv">
                                <span className="ci-k">Paquete</span>
                                <span className="ci-v">{pickedVisit.package.packageType}</span>
                            </div>
                        )}
                    </div>

                    {/* ── Behavior / Mood — 3-face selector ── */}
                    <div className="co-mood-wrap">
                        <label className="ci-notes-label">¿Cómo se portó? <span className="co-mood-optional">(opcional)</span></label>
                        <div className="co-mood-grid">
                            {CHECKOUT_MOODS.map(m => {
                                const isSelected = checkoutMood === m.value;
                                return (
                                    <button
                                        key={m.value}
                                        type="button"
                                        className={`co-mood-card${isSelected ? ' co-mood-card--selected' : ''}`}
                                        style={{
                                            background: isSelected ? m.bg : '#ffffff',
                                            borderColor: isSelected ? m.border : '#e5e7eb',
                                            color: m.color,
                                        }}
                                        onClick={() => setCheckoutMood(isSelected ? null : m.value)}
                                        aria-pressed={isSelected}
                                    >
                                        <span className="co-mood-emoji" aria-hidden>{m.emoji}</span>
                                        <span className="co-mood-label">{m.label}</span>
                                        <span className="co-mood-desc">{m.desc}</span>
                                        {isSelected && <span className="co-mood-check">✓</span>}
                                    </button>
                                );
                            })}
                        </div>
                        {checkoutMood && (
                            <div className="co-behavior-wrap">
                                <label className="ci-notes-label">Describe qué pasó <span className="co-mood-optional">(para este ánimo)</span></label>
                                <textarea
                                    className="ci-notes co-behavior-notes"
                                    rows={2}
                                    maxLength={1000}
                                    placeholder={
                                        checkoutMood === 'happy' ? 'Ej: Jugó todo el día, muy sociable con otros perros…' :
                                        checkoutMood === 'neutral' ? 'Ej: Un poco inquieto al inicio, luego se calmó…' :
                                        'Ej: Ladridos frecuentes, no quiso comer, se aisló…'
                                    }
                                    value={checkoutBehaviorNotes}
                                    onChange={e => setCheckoutBehaviorNotes(e.target.value)}
                                />
                                <span className="co-char-count">{checkoutBehaviorNotes.length}/1000</span>
                            </div>
                        )}
                    </div>

                    <div className="ci-notes-wrap">
                        <label className="ci-notes-label">Notas de salida</label>
                        <textarea
                            className="ci-notes"
                            rows={3}
                            placeholder="Comidas, incidentes, notas para el propietario…"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                        />
                    </div>

                    <div className="ci-actions">
                        {editingVisit
                            ? <button className="btn-secondary" onClick={onCancel}>Cancelar</button>
                            : <button className="btn-secondary" onClick={() => setStep(1)}>Atrás</button>
                        }
                        <button
                            className="btn-primary co-confirm-btn"
                            onClick={handleConfirm}
                            disabled={loading}
                        >
                            {loading ? 'Registrando salida…' : 'Confirmar salida'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CheckOutWizard;
